/**
 * A multi-select that works with a thumb.
 *
 * A `<select multiple>` needs modifier keys and precise taps, neither of which
 * exists on a phone, so a list of large checkbox rows replaces it. The same
 * control is used on a wide screen — it is simply easier there too.
 */
export interface MultiCheckboxOption {
  value: string;
  label: string;
}

export function MultiCheckboxList({
  options,
  selected,
  onChange,
  emptyLabel = 'Nothing to choose from.',
  className = '',
}: {
  options: MultiCheckboxOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  emptyLabel?: string;
  className?: string;
}) {
  if (options.length === 0) {
    return <p className="px-3 py-4 text-sm text-ink-muted">{emptyLabel}</p>;
  }

  return (
    <div
      className={`max-h-56 overflow-y-auto rounded-sm border border-surface-border bg-white ${className}`}
    >
      {options.map((option) => {
        const checked = selected.includes(option.value);
        return (
          <label
            key={option.value}
            className="flex cursor-pointer items-center gap-3 border-b border-surface-muted px-3 py-3 last:border-b-0 active:bg-surface"
          >
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0"
              checked={checked}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...selected, option.value]
                    : selected.filter((value) => value !== option.value),
                )
              }
            />
            <span className="min-w-0 text-sm text-ink-body">{option.label}</span>
          </label>
        );
      })}
    </div>
  );
}
